export type SettlementPermissions = {
  viewQueue: boolean;
  evaluateEligibility: boolean;
  createDraft: boolean;
  manualReview: boolean;
  submitReview: boolean;
  approve: boolean;
  reject: boolean;
  correct: boolean;
  manageFeeRules: boolean;
  viewClientSafe: boolean;
  exportReport: boolean;
};

export type SettlementMetrics = {
  settlementCount: number;
  grossQualifyingRecoveryCents: number;
  recoveryFeeCents: number;
  totalClientInvoiceCents: number;
  draftSettlements: number;
  awaitingApproval: number;
  approvedSettlements: number;
  exceptionSettlements: number;
  eligiblePayments: number;
  pendingReview: number;
  eligibilityExceptions: number;
  evaluatedQualifyingCents: number;
  invoiceDrafts: number;
  sentInvoices: number;
  paidInvoices: number;
  overdueInvoices: number;
};

export type SettlementRecord = {
  settlementId: string;
  companyId: string;
  clientId: string;
  clientName: string;
  settlementNumber: string;
  settlementPeriodStart: string;
  settlementPeriodEnd: string;
  settlementStatus: string;
  approvalStatus: string;
  currency: string;
  grossQualifyingRecoveryCents: number;
  excludedNonQualifyingRecoveryCents: number;
  adjustmentCents: number;
  netQualifyingRecoveryCents: number;
  recoveryFeeCents: number;
  taxCents: number;
  totalClientInvoiceCents: number;
  calculationVersion: number;
  preparedByUserId?: string | null;
  preparedByName: string;
  lastFinancialModifierUserId?: string | null;
  lastFinancialModifierName?: string | null;
  feeOverriderUserId?: string | null;
  feeOverriderName?: string | null;
  reviewedByName?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  reversedAt?: string | null;
  reversedByUserId?: string | null;
  reversedByName?: string | null;
  reversalReason?: string | null;
  reversedFromSettlementId?: string | null;
  reversedFromSettlementNumber?: string | null;
  replacementSettlementId?: string | null;
  replacementSettlementNumber?: string | null;
  correctedFromSettlementId?: string | null;
  lineCount: number;
  version: number;
  updatedAt: string;
  createdAt: string;
};

export type SettlementEligibility = {
  eligibilityId: string;
  companyId: string;
  clientId: string;
  clientName: string;
  customerId?: string | null;
  customerName?: string | null;
  accountId?: string | null;
  accountNumber?: string | null;
  placementId?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  paymentId: string;
  paymentReference?: string | null;
  allocationId?: string | null;
  feeRuleId?: string | null;
  feeRuleName?: string | null;
  feeRuleVersion?: number | null;
  feeRuleType?: string | null;
  classification: string;
  reasonCode: string;
  reasonDetail: string;
  paymentAmountCents: number;
  allocationAmountCents: number;
  qualifyingAmountCents: number;
  nonQualifyingAmountCents: number;
  estimatedFeeCents?: number | null;
  estimatedFeeExplanation?: string | null;
  currency: string;
  paymentDate?: string | null;
  sourceStatus?: string | null;
  evaluatedByName: string;
  evaluatedAt: string;
  sourceIdentityKey?: string | null;
  isCurrent?: boolean;
};

export type SettlementLine = {
  lineId: string;
  settlementId: string;
  paymentId: string;
  allocationId?: string | null;
  clientId: string;
  customerId?: string | null;
  accountId?: string | null;
  placementId?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  paymentDate?: string | null;
  verifiedAmountCents: number;
  qualifyingAmountCents: number;
  nonQualifyingAmountCents: number;
  feeRuleId?: string | null;
  feeRuleVersion: number;
  feeRuleType?: string | null;
  feeRateBasisPoints: number;
  flatFeeCents?: number | null;
  minimumFeeCents?: number | null;
  maximumFeeCents?: number | null;
  ageBucketFromDays?: number | null;
  ageBucketToDays?: number | null;
  feeRuleCurrency?: string | null;
  feeRuleEffectiveDate?: string | null;
  feeRuleExpirationDate?: string | null;
  minApplied?: boolean;
  maxApplied?: boolean;
  feeBasis: string;
  calculatedFeeCents: number;
  adjustmentCents: number;
  exclusionReason?: string | null;
  calculationExplanation: string;
  calculationEvidence?: Record<string, unknown>;
  currency: string;
};

export type SettlementInvoiceDraft = {
  invoiceDraftId: string;
  settlementId: string;
  clientId: string;
  clientName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  paymentTerms: string;
  currency: string;
  subtotalCents: number;
  adjustmentCents: number;
  taxCents: number;
  totalDueCents: number;
  invoiceStatus: string;
  delinquencyStatus: string;
  reminderStatus: string;
  followUpStatus: string;
  placementPauseRecommendation: string;
  approvalHistory: unknown[];
  deliveryHistory: unknown[];
  billingContact: string;
  version: number;
  updatedAt: string;
};

export type SettlementHistoryEntry = {
  actionId: string;
  actionType: string;
  previousStatus?: string | null;
  newStatus?: string | null;
  actorName: string;
  actorRole: string;
  reason?: string | null;
  metadata: Record<string, unknown>;
  correlationId: string;
  occurredAt: string;
};

export type SettlementFeeRule = {
  feeRuleId: string;
  companyId: string;
  clientId: string;
  clientName: string;
  placementId?: string | null;
  ruleName: string;
  ruleType: string;
  feeBasis: string;
  feeRateBasisPoints?: number | null;
  flatFeeCents?: number | null;
  minimumFeeCents?: number | null;
  maximumFeeCents?: number | null;
  ageBucketFromDays?: number | null;
  ageBucketToDays?: number | null;
  effectiveDate: string;
  expirationDate?: string | null;
  currency: string;
  status: string;
  changeReason: string;
  version: number;
  createdByName?: string | null;
  updatedByName?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SettlementFeeRuleVersion = {
  feeRuleVersionId: string;
  feeRuleId: string;
  companyId: string;
  clientId: string;
  placementId?: string | null;
  ruleName: string;
  ruleType: string;
  feeBasis: string;
  feeRateBasisPoints?: number | null;
  flatFeeCents?: number | null;
  minimumFeeCents?: number | null;
  maximumFeeCents?: number | null;
  ageBucketFromDays?: number | null;
  ageBucketToDays?: number | null;
  effectiveDate: string;
  expirationDate?: string | null;
  currency: string;
  status: string;
  changeReason: string;
  version: number;
  createdAt: string;
  snapshot: Record<string, unknown>;
};

export type SettlementClientOption = {
  clientId: string;
  clientName: string;
  currency?: string | null;
};

export type SettlementDetail = {
  settlement: SettlementRecord;
  lines: SettlementLine[];
  eligibility: SettlementEligibility[];
  actions: SettlementHistoryEntry[];
  audit: Array<{
    auditId: string;
    operation: string;
    previousVersion?: number | null;
    resultingVersion?: number | null;
    previousValues: Record<string, unknown>;
    newValues: Record<string, unknown>;
    reason?: string | null;
    actorName: string;
    actorRole: string;
    outcome: string;
    correlationId: string;
    occurredAt: string;
  }>;
  versions: Array<{
    versionId: string;
    settlementId: string;
    version: number;
    calculationVersion: number;
    snapshot: Record<string, unknown>;
    reason: string;
    actorName: string;
    createdAt: string;
  }>;
  invoiceDraft: SettlementInvoiceDraft | null;
  statementVersions: Array<{
    statementVersionId: string;
    settlementId: string;
    clientId: string;
    statementNumber: string;
    version: number;
    sourceSettlementVersion: number;
    documentStatus: string;
    mimeType: string;
    fileName: string;
    isCurrent: boolean;
    generatedByName: string;
    generatedAt: string;
    document: Record<string, unknown>;
  }>;
  delinquencyHistory: Array<{
    historyId: string;
    invoiceDraftId: string;
    settlementId: string;
    clientId: string;
    previousValues: Record<string, unknown>;
    newValues: Record<string, unknown>;
    reason: string;
    actorName: string;
    actorRole: string;
    correlationId: string;
    occurredAt: string;
  }>;
  permissions: SettlementPermissions;
  currentUserId?: string | null;
};

export type SettlementQueueFilters = {
  companyId?: string;
  search?: string;
  clientId?: string;
  accountId?: string;
  placementId?: string;
  status?: string;
  approvalStatus?: string;
  invoiceStatus?: string;
  periodStart?: string;
  periodEnd?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
};

export type SettlementEligibilityFilters = {
  companyId?: string;
  clientId?: string;
  currency?: string;
  accountId?: string;
  placementId?: string;
  periodStart?: string;
  periodEnd?: string;
  classification?: string;
  limit?: number;
};

export type SettlementFeeRuleInput = {
  clientId: string;
  placementId?: string | null;
  ruleName: string;
  ruleType?: string;
  feeBasis?: string;
  feeRateBasisPoints?: number | null;
  flatFeeCents?: number | null;
  minimumFeeCents?: number | null;
  maximumFeeCents?: number | null;
  ageBucketFromDays?: number | null;
  ageBucketToDays?: number | null;
  effectiveDate: string;
  expirationDate?: string | null;
  currency?: string;
  status?: string;
  changeReason: string;
  expectedVersion?: number;
};

export async function listSettlements(filters: SettlementQueueFilters = {}) {
  return settlementRequest<{
    settlements: SettlementRecord[];
    metrics: SettlementMetrics;
    eligibility: SettlementEligibility[];
    invoiceDrafts: SettlementInvoiceDraft[];
    clients: SettlementClientOption[];
    pagination: { page: number; pageSize: number; total: number; pageCount: number };
    permissions: SettlementPermissions;
    currentUserId?: string | null;
  }>(`/api/ar-recovery/settlements?${params(filters)}`);
}

export async function getSettlement(settlementId: string, companyId?: string) {
  return settlementRequest<SettlementDetail>(`/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}?${params({ companyId })}`);
}

export async function listSettlementEligibility(filters: SettlementEligibilityFilters = {}) {
  return settlementRequest<{
    eligibility: SettlementEligibility[];
    clients: SettlementClientOption[];
    permissions: SettlementPermissions;
    currentUserId?: string | null;
  }>(`/api/ar-recovery/settlements/eligibility?${params(filters)}`);
}

export async function evaluateSettlementEligibility(companyId?: string) {
  return settlementRequest<{ decisions: SettlementEligibility[]; metrics: SettlementMetrics }>("/api/ar-recovery/settlements", {
    method: "POST",
    body: JSON.stringify({ companyId, action: "evaluate" }),
  });
}

export async function createSettlementDraft(
  draft: { clientId: string; settlementPeriodStart: string; settlementPeriodEnd: string; eligibilityIds: string[]; reason: string },
  companyId?: string,
) {
  return settlementRequest<{ settlement: SettlementRecord; created: boolean; replay: boolean }>("/api/ar-recovery/settlements", {
    method: "POST",
    body: JSON.stringify({ companyId, action: "create_draft", draft: { ...draft, idempotencyKey: `settlement-draft:${draft.clientId}:${draft.settlementPeriodStart}:${draft.settlementPeriodEnd}:${draft.eligibilityIds.join("|")}:${crypto.randomUUID()}` } }),
  });
}

export async function transitionSettlement(
  settlementId: string,
  input: { action: "submit_review" | "return_correction" | "approve" | "reject" | "void" | "reverse" | "create_replacement"; reason: string; expectedVersion: number },
  companyId?: string,
) {
  return settlementRequest<SettlementDetail>(`/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}/actions`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement:${settlementId}:${input.action}:${input.expectedVersion}:${crypto.randomUUID()}` }),
  });
}

export async function applySettlementManualReview(
  settlementId: string,
  input: {
    action: "confirm_eligibility" | "mark_ineligible" | "pending_review" | "exclude_line" | "adjust_qualifying_amount" | "fee_override" | "add_explanation" | "return_source_payment" | "submit_approval" | "reject_calculation";
    reason: string;
    expectedVersion: number;
    lineId?: string | null;
    eligibilityId?: string | null;
    qualifyingAmountCents?: number | null;
    feeRateBasisPoints?: number | null;
    explanation?: string | null;
  },
  companyId?: string,
) {
  return settlementRequest<SettlementDetail>(`/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}/manual-review`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement-manual:${settlementId}:${input.action}:${input.expectedVersion}:${crypto.randomUUID()}` }),
  });
}

export async function applySettlementAdjustment(
  settlementId: string,
  input: { adjustmentCents: number; reason: string; expectedVersion: number },
  companyId?: string,
) {
  return settlementRequest<SettlementDetail>(`/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}/adjustments`, {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement-adjustment:${settlementId}:${input.expectedVersion}:${crypto.randomUUID()}` }),
  });
}

export async function listSettlementFeeRules(companyId?: string) {
  return settlementRequest<{ feeRules: SettlementFeeRule[]; permissions: SettlementPermissions; currentUserId?: string | null }>(
    `/api/ar-recovery/settlements/fee-rules?${params({ companyId })}`,
  );
}

export async function getSettlementFeeRule(feeRuleId: string, companyId?: string) {
  return settlementRequest<{ feeRule: SettlementFeeRule; permissions: SettlementPermissions }>(
    `/api/ar-recovery/settlements/fee-rules/${encodeURIComponent(feeRuleId)}?${params({ companyId })}`,
  );
}

export async function createSettlementFeeRule(input: SettlementFeeRuleInput, companyId?: string) {
  return settlementRequest<{ feeRule: SettlementFeeRule; created: boolean; replay: boolean }>("/api/ar-recovery/settlements/fee-rules", {
    method: "POST",
    body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement-fee-rule:${input.clientId}:${input.ruleName}:${input.effectiveDate}:${crypto.randomUUID()}` }),
  });
}

export async function updateSettlementFeeRule(feeRuleId: string, input: SettlementFeeRuleInput, companyId?: string) {
  return settlementRequest<{ feeRule: SettlementFeeRule; updated: boolean; replay: boolean }>(
    `/api/ar-recovery/settlements/fee-rules/${encodeURIComponent(feeRuleId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement-fee-rule-update:${feeRuleId}:${input.expectedVersion}:${crypto.randomUUID()}` }),
    },
  );
}

export async function listSettlementFeeRuleHistory(feeRuleId: string, companyId?: string) {
  return settlementRequest<{
    versions: SettlementFeeRuleVersion[];
    history: SettlementDetail["audit"];
  }>(`/api/ar-recovery/settlements/fee-rules/${encodeURIComponent(feeRuleId)}/history?${params({ companyId })}`);
}

export async function generateSettlementStatement(
  settlementId: string,
  input: { reason: string; expectedVersion: number },
  companyId?: string,
) {
  return settlementRequest<{ statementVersion: SettlementDetail["statementVersions"][number]; replay: boolean }>(
    `/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}/statements`,
    {
      method: "POST",
      body: JSON.stringify({ companyId, ...input, idempotencyKey: `settlement-statement:${settlementId}:${input.expectedVersion}:${crypto.randomUUID()}` }),
    },
  );
}

export function settlementStatementUrl(settlementId: string, versionId: string, options: { download?: boolean; format?: "html" | "text" } = {}) {
  const query = params({ download: options.download ? "1" : "", format: options.format || "" });
  return `/api/ar-recovery/settlements/${encodeURIComponent(settlementId)}/statements/${encodeURIComponent(versionId)}${query ? `?${query}` : ""}`;
}

export async function getSettlementReports(companyId?: string) {
  return settlementRequest<{
    metrics: SettlementMetrics;
    byStatus: Array<{ label: string; count: number; amountCents: number }>;
    byClient: Array<{ label: string; count: number; amountCents: number }>;
    byEligibility: Array<{ label: string; count: number; amountCents: number }>;
    invoices: Array<{ label: string; count: number; amountCents: number }>;
  }>(`/api/ar-recovery/settlements/reports?${params({ companyId })}`);
}

async function settlementRequest<T>(url: string, init: RequestInit = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.text()) || "Settlement Center request failed.");
  return response.json() as Promise<T>;
}

function params(input: Record<string, string | number | null | undefined>) {
  const result = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value != null && value !== "") result.set(key, String(value));
  });
  return result.toString();
}
